import {
	Alert,
	AsyncSelect,
	Badge,
	Box,
	Button,
	Col,
	Divider,
	Grid,
	Heading,
	Icons,
	Inline,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import {
	arrayMutators,
	CheckboxInputField,
	Form,
	FormApi,
	GetFieldValue,
	SelectField,
	SelectInputField,
	SubmissionError,
	TextAreaInputField,
	TextInputField,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { IListResponse, useXHR } from "@sembark-travel/xhr"
import { useCallback, useEffect, useState } from "react"
import {
	SelectWAFlow,
	SelectWAFlowScreen,
	SelectWAPhoneNumber,
	SelectWATemplate,
	TWAFlow,
	TWATemplate,
	TWhatsappBusinessAccountPhoneNumber,
} from "../Integrations"
import { IUser, SelectUsers } from "../Users"
import { SelectWAQuickSend } from "../WhatsAppQuickSends"
import {
	TAutomation,
	TAutomationActionStepConfig,
	TAutomationConditionStepConfig,
	TAutomationPayload,
	TAutomationRecipientResolver,
	TAutomationSource,
	TAutomationStartType,
	TAutomationWaitStepConfig,
	XHR,
	TAutomationEventStart,
	TAutomationScheduleStart,
	type TAutomationCheckpointFormValue,
	type TAutomationOffsetFormValue,
	type TAutomationEveryFormValue,
	parseCheckpointString,
	checkpointToOffset,
	createOffsetFormValue,
	createEveryFormValue,
	parseOffsetString,
	parseEveryString,
	offsetToString,
	everyToString,
} from "./store"
import {
	CheckpointsField,
	IntervalWindowField,
	SendWindowField,
} from "./CheckpointsField"
import { RuleGroupEditor } from "./RuleGroupEditor"
import {
	buildRuleGroup,
	createId,
	createRuleGroupFormValue,
	formatFieldLabel,
	formatFieldOptionLabel,
	ruleGroupFormValueFromGroup,
	summarizeRuleGroup,
	trimmedString,
	validateRuleGroupFormValue,
	type TAutomationActionType,
	type TAutomationDefinitionStepFormValue,
	type TAutomationRecipientRowFormValue,
	type TAutomationRuleGroupFormValue,
	type TAutomationTaskAssignmentFormValue,
} from "./definition-form"
import { Required } from "utility-types"

type TAutomationDefinitionFormValues = {
	source: TAutomationSource | null
	start_type?: TAutomationStartType
	event?: string
	// Event start on a source that supports it; empty means every time.
	cooldown_minutes?: string
	start_filters?: TAutomationRuleGroupFormValue
	schedule_field?: string
	schedule_checkpoints: Array<TAutomationCheckpointFormValue>
	enable_interval_window?: boolean
	interval_start_offset?: TAutomationOffsetFormValue
	interval_end_offset?: TAutomationOffsetFormValue
	interval_every?: TAutomationEveryFormValue
	send_window?: { from?: string; to?: string }
	steps: Array<TAutomationDefinitionStepFormValue>
}

function AutomationFormSection({
	title,
	description,
	children,
}: {
	title: string
	description?: string
	children: React.ReactNode
}) {
	return (
		<Box borderWidth="1" rounded="md" padding="4">
			<Stack gap="4">
				<Stack gap="1">
					<Heading as="h3" fontSize="lg">
						{title}
					</Heading>
					{description ? (
						<Text color="muted" fontSize="sm">
							{description}
						</Text>
					) : null}
				</Stack>
				{children}
			</Stack>
		</Box>
	)
}

function createRecipientRowFormValue(
	partial?: Partial<TAutomationRecipientRowFormValue>
): TAutomationRecipientRowFormValue {
	return {
		id: createId("recipient"),
		bucket: "to",
		path: "",
		...partial,
	}
}

function emptyUser(userId: number): IUser {
	return {
		id: userId,
		uid: String(userId),
		name: `User #${String(userId)}`,
		email: "",
		invited_at: "",
		created_at: "",
		updated_at: "",
		is_owner: false,
	}
}

function slugifyStepKey(value: string | undefined) {
	return (value || "")
		.toLowerCase()
		.replace(/[^a-z0-9_]+/g, "_")
		.replace(/_{2,}/g, "_")
}

function uniqueStepKey(
	baseKey: string,
	steps: Array<TAutomationDefinitionStepFormValue>,
	currentStepId?: string
) {
	const normalizedBase = slugifyStepKey(baseKey) || "step"
	const siblingKeys = new Set(
		steps
			.filter((step) => step.id !== currentStepId)
			.map((step) => step.key)
			.filter(Boolean)
	)

	if (!siblingKeys.has(normalizedBase)) {
		return normalizedBase
	}

	let suffix = 2
	let candidate = `${normalizedBase}_${String(suffix)}`

	while (siblingKeys.has(candidate)) {
		suffix += 1
		candidate = `${normalizedBase}_${String(suffix)}`
	}

	return candidate
}

function suggestedStepKey(
	step: TAutomationDefinitionStepFormValue,
	source?: TAutomationSource | null
) {
	if (step.type === "wait") {
		if (step.wait_mode === "relative_to_field" && step.wait_field) {
			const fieldLabel = formatFieldLabel(source, step.wait_field)

			return `wait_until_${slugifyStepKey(fieldLabel)}`
		}

		return "wait"
	}

	if (step.type === "condition") {
		return "check_condition"
	}

	if (step.action_type === "create_task") {
		return step.task_assignment?.mode === "none"
			? "create_task"
			: "create_followup_task"
	}

	if (step.action_type === "create_trip_plan_request") {
		return "create_trip_plan_request"
	}

	if (step.action_type === "send_whatsapp_flow") {
		return "offer_whatsapp_form"
	}

	if (step.action_type === "send_wa_quick_send") {
		return "send_quick_send"
	}

	if (step.action_type === "send_whatsapp_text") {
		return "send_whatsapp_text"
	}

	if (
		step.communication_channels?.email &&
		!step.communication_channels?.whatsapp
	) {
		return "send_email_message"
	}

	if (step.communication_channels?.whatsapp && step.wa_template) {
		return "send_whatsapp_template"
	}

	return "send_whatsapp_message"
}

function normalizeStepKey(
	step: TAutomationDefinitionStepFormValue,
	steps: Array<TAutomationDefinitionStepFormValue>,
	source?: TAutomationSource | null
): TAutomationDefinitionStepFormValue {
	if (step.is_key_customized && trimmedString(step.key)) {
		return {
			...step,
			key: uniqueStepKey(trimmedString(step.key), steps, step.id),
		}
	}

	return {
		...step,
		key: uniqueStepKey(suggestedStepKey(step, source), steps, step.id),
	}
}

function normalizeStepsForKeys(
	steps: Array<TAutomationDefinitionStepFormValue>,
	source?: TAutomationSource | null
) {
	return steps.map((step, _index, currentSteps) =>
		normalizeStepKey(step, currentSteps, source)
	)
}

function getChildSteps(
	steps: Array<TAutomationDefinitionStepFormValue>,
	parentStepId: string | null
) {
	return steps.filter((step) => step.parent_step_id === parentStepId)
}

function getDescendantStepIds(
	steps: Array<TAutomationDefinitionStepFormValue>,
	stepId: string
): Array<string> {
	const directChildren = steps.filter((step) => step.parent_step_id === stepId)

	return directChildren.flatMap((child) => [
		child.id,
		...getDescendantStepIds(steps, child.id),
	])
}

function removeStepTree(
	steps: Array<TAutomationDefinitionStepFormValue>,
	stepId: string
) {
	const idsToRemove = new Set([stepId, ...getDescendantStepIds(steps, stepId)])
	return steps.filter((step) => !idsToRemove.has(step.id))
}

function stepDepth(
	steps: Array<TAutomationDefinitionStepFormValue>,
	step: TAutomationDefinitionStepFormValue
) {
	let depth = 0
	let currentParentId = step.parent_step_id

	while (currentParentId) {
		const id = currentParentId // make eslint happy by storing local id
		const parent = steps.find((item) => item.id === id)
		if (!parent) {
			break
		}

		depth += 1
		currentParentId = parent.parent_step_id
	}

	return depth
}

function insertStepInsideCondition(
	steps: Array<TAutomationDefinitionStepFormValue>,
	conditionStepId: string,
	newStep: TAutomationDefinitionStepFormValue
) {
	const descendants = getDescendantStepIds(steps, conditionStepId)
	const lastDescendantId = descendants[descendants.length - 1]
	const insertAfterId = lastDescendantId || conditionStepId
	const insertIndex = steps.findIndex((step) => step.id === insertAfterId)
	const next = [...steps]

	next.splice(insertIndex + 1, 0, {
		...newStep,
		parent_step_id: conditionStepId,
	})

	return next
}

function buildNestedStepsPayload(
	steps: Array<TAutomationDefinitionStepFormValue>,
	source: TAutomationSource,
	parentStepId: string | null = null
): TAutomationPayload["steps"] {
	return getChildSteps(steps, parentStepId).map((step) => {
		const base = {
			key: slugifyStepKey(trimmedString(step.key)) || "step",
			type: step.type || "action",
		} as TAutomationPayload["steps"][number]

		if (step.type === "wait") {
			return {
				...base,
				config: {
					mode: step.wait_mode || "relative_to_trigger",
					...(step.wait_mode === "relative_to_field"
						? { field: trimmedString(step.wait_field) }
						: {}),
					offset:
						offsetToString({
							...(step.wait_offset || {}),
							direction:
								step.wait_mode === "relative_to_trigger"
									? "after"
									: step.wait_offset?.direction,
						}) || "",
				} satisfies TAutomationWaitStepConfig,
			}
		}

		if (step.type === "condition") {
			return {
				...base,
				config: buildRuleGroup(
					step.condition_rules || createRuleGroupFormValue(),
					source.filter_fields
				) satisfies TAutomationConditionStepConfig,
				children: buildNestedStepsPayload(steps, source, step.id),
			}
		}

		if (step.action_type === "send_whatsapp_flow") {
			return {
				...base,
				type: "action",
				config: {
					action: {
						type: "send_whatsapp_flow",
						recipients: recipientsPayload(step),
						flow: {
							flow_id: trimmedString(step.wa_flow_id),
							cta: trimmedString(step.wa_flow_cta),
							body: trimmedString(step.wa_flow_body),
							...(trimmedString(step.wa_flow_header)
								? { header: trimmedString(step.wa_flow_header) }
								: {}),
							...(trimmedString(step.wa_flow_footer)
								? { footer: trimmedString(step.wa_flow_footer) }
								: {}),
							screen: trimmedString(step.wa_flow_screen?.id),
							// A source that answers a conversation sends from
							// the number the customer wrote to, so it stores
							// no sender of its own.
							...(source.replies_into_conversation
								? {}
								: {
										phone_number_id: Number(step.wa_phone_number?.id),
									}),
						},
					},
				} satisfies TAutomationActionStepConfig,
			}
		}

		if (step.action_type === "send_whatsapp_text") {
			return {
				...base,
				type: "action",
				config: {
					action: {
						type: "send_whatsapp_text",
						recipients: recipientsPayload(step),
						text: {
							body: trimmedString(step.wa_text_body),
							// A source that answers a conversation sends from
							// the number the customer wrote to, so it stores
							// no sender of its own.
							...(source.replies_into_conversation
								? {}
								: {
										phone_number_id: Number(step.wa_phone_number?.id),
									}),
						},
					},
				} satisfies TAutomationActionStepConfig,
			}
		}

		if (step.action_type === "send_wa_quick_send") {
			return {
				...base,
				type: "action",
				config: {
					action: {
						type: "send_wa_quick_send",
						recipients: recipientsPayload(step),
						quick_send: {
							quick_send_id: Number(step.wa_quick_send?.id),
							// A source that answers a conversation sends from
							// the number the customer wrote to, so it stores
							// no sender of its own.
							...(source.replies_into_conversation
								? {}
								: {
										phone_number_id: Number(step.wa_phone_number?.id),
									}),
						},
					},
				} satisfies TAutomationActionStepConfig,
			}
		}

		if (step.action_type === "create_trip_plan_request") {
			return {
				...base,
				type: "action",
				config: {
					action: {
						type: "create_trip_plan_request",
						...(trimmedString(step.trip_source)
							? { trip_source: trimmedString(step.trip_source) }
							: {}),
					},
				} satisfies TAutomationActionStepConfig,
			}
		}

		if (step.action_type === "create_task") {
			return {
				...base,
				type: "action",
				config: {
					action: {
						type: "create_task",
						body_template: step.task_body_template || "",
						...(step.task_assignment?.mode === "static_user" &&
						step.task_assignment?.user
							? {
									assign_to: {
										type: "static_user",
										user_id: step.task_assignment.user.id,
									},
								}
							: step.task_assignment?.mode === "field_path" &&
								  step.task_assignment?.field
								? {
										assign_to: {
											type: "field_path",
											path: step.task_assignment.field,
										},
									}
								: {}),
						...(step.task_due?.enabled &&
						offsetToString({
							...(step.task_due?.offset || {}),
							direction:
								step.task_due?.mode === "relative_to_trigger"
									? "after"
									: step.task_due?.offset?.direction,
						})
							? {
									due:
										step.task_due?.mode === "relative_to_field"
											? {
													mode: "relative_to_field" as const,
													field: step.task_due?.field || "",
													offset: offsetToString(step.task_due?.offset) || "",
												}
											: {
													mode: "relative_to_trigger" as const,
													offset:
														offsetToString({
															...(step.task_due?.offset || {}),
															direction: "after",
														}) || "",
												},
								}
							: {}),
					},
				} satisfies TAutomationActionStepConfig,
			}
		}

		return {
			...base,
			type: "action",
			config: {
				action: {
					type: "communication",
					recipients: recipientsPayload(step),
					deliveries:
						step.communication_channels?.whatsapp &&
						step.wa_template &&
						(step.wa_phone_number || source.replies_into_conversation)
							? [
									{
										channel: "whatsapp",
										template_id: step.wa_template.id,
										// Answering a conversation, the sender is
										// the number the customer wrote to.
										...(source.replies_into_conversation
											? {}
											: {
													phone_number_id: step.wa_phone_number?.id,
												}),
									},
								]
							: [],
				},
			} satisfies TAutomationActionStepConfig,
		}
	})
}

/**
 * The step's recipient rows as the API's buckets. Shared by every step that
 * messages someone: communication and the WhatsApp form.
 */
function recipientsPayload(
	step: TAutomationDefinitionStepFormValue
): NonNullable<
	Extract<
		TAutomationActionStepConfig["action"],
		{ type: "communication" }
	>["recipients"]
> {
	return step.recipients.reduce<
		NonNullable<
			Extract<
				TAutomationActionStepConfig["action"],
				{ type: "communication" }
			>["recipients"]
		>
	>((acc, recipient) => {
		if (!trimmedString(recipient.path)) {
			return acc
		}

		const bucket = recipient.bucket || "to"
		const nextBucket = [...(acc[bucket] || [])]
		nextBucket.push({
			type: "field_path",
			path: trimmedString(recipient.path),
			recipient_type: "phone",
		} satisfies TAutomationRecipientResolver)

		return {
			...acc,
			[bucket]: nextBucket,
		}
	}, {})
}

function createCommunicationStepFormValue(
	source?: TAutomationSource | null
): TAutomationDefinitionStepFormValue {
	const defaultRecipient = source?.recipient_fields.find((field) =>
		field.recipient_types?.includes("phone")
	)

	return {
		id: createId("step"),
		parent_step_id: null,
		key: "",
		is_key_customized: false,
		is_key_editing: false,
		type: "action",
		action_type: "communication",
		wait_mode: "relative_to_trigger",
		wait_field: "",
		wait_offset: createOffsetFormValue({ amount: "1", unit: "days" }),
		condition_rules: createRuleGroupFormValue(),
		communication_channels: {
			whatsapp: true,
			email: false,
		},
		recipients: defaultRecipient
			? [createRecipientRowFormValue({ path: defaultRecipient.key })]
			: [createRecipientRowFormValue()],
		wa_template: null,
		wa_phone_number: null,
		wa_flow: null,
		wa_flow_id: "",
		wa_flow_cta: "",
		wa_flow_body: "",
		wa_flow_header: "",
		wa_flow_footer: "",
		wa_flow_screen: null,
		wa_quick_send: null,
		wa_text_body: "",
		trip_source: "",
		task_body_template: "",
		task_assignment: {
			mode: "none",
			user: null,
			field: "",
		},
		task_due: {
			enabled: false,
			mode: "relative_to_trigger",
			field: "",
			offset: createOffsetFormValue({ amount: "1", unit: "hours" }),
		},
	}
}

function createWaitStepFormValue(): TAutomationDefinitionStepFormValue {
	return {
		id: createId("step"),
		parent_step_id: null,
		key: "",
		is_key_customized: false,
		is_key_editing: false,
		type: "wait",
		action_type: "communication",
		wait_mode: "relative_to_trigger",
		wait_field: "",
		wait_offset: createOffsetFormValue({ amount: "1", unit: "hours" }),
		condition_rules: createRuleGroupFormValue(),
		communication_channels: {
			whatsapp: true,
			email: false,
		},
		recipients: [createRecipientRowFormValue()],
		wa_template: null,
		wa_phone_number: null,
		wa_flow: null,
		wa_flow_id: "",
		wa_flow_cta: "",
		wa_flow_body: "",
		wa_flow_header: "",
		wa_flow_footer: "",
		wa_flow_screen: null,
		wa_quick_send: null,
		wa_text_body: "",
		trip_source: "",
		task_body_template: "",
		task_assignment: {
			mode: "none",
			user: null,
			field: "",
		},
		task_due: {
			enabled: false,
			mode: "relative_to_trigger",
			field: "",
			offset: createOffsetFormValue({ amount: "1", unit: "hours" }),
		},
	}
}

function createConditionStepFormValue(): TAutomationDefinitionStepFormValue {
	return {
		...createWaitStepFormValue(),
		type: "condition",
		condition_rules: createRuleGroupFormValue(),
	}
}

function createTaskStepFormValue(): TAutomationDefinitionStepFormValue {
	return {
		...createCommunicationStepFormValue(),
		action_type: "create_task",
		task_body_template: "Follow up on {{customer_name}}.",
	}
}

function createTripPlanRequestStepFormValue(): TAutomationDefinitionStepFormValue {
	return {
		...createCommunicationStepFormValue(),
		action_type: "create_trip_plan_request",
		trip_source: "",
	}
}

function describeOffset(
	offset: TAutomationOffsetFormValue,
	basis: string,
	allowBefore = true
) {
	const amount = trimmedString(offset.amount) || "?"

	if (Number(amount) === 0) {
		return basis
	}

	if (!allowBefore || offset.direction === "after") {
		return `${amount} ${offset.unit} after ${basis}`
	}

	return `${amount} ${offset.unit} before ${basis}`
}

function summarizeRecipients(
	source: TAutomationSource | null | undefined,
	recipients: Array<TAutomationRecipientRowFormValue>
) {
	const labels = recipients
		.filter(
			(recipient): recipient is Required<typeof recipient, "path" | "bucket"> =>
				Boolean(recipient.path && recipient.bucket)
		)
		.map(
			(recipient) =>
				`${recipient.bucket.toUpperCase()}: ${formatFieldLabel(source, recipient.path)}`
		)

	return labels.length ? labels.join(" • ") : "No recipients selected yet."
}

function summarizeTaskAssignment(
	source: TAutomationSource | null | undefined,
	assignment: TAutomationTaskAssignmentFormValue
) {
	if (assignment.mode === "static_user" && assignment.user) {
		return `Assigned to ${assignment.user.name}.`
	}

	if (assignment.mode === "field_path" && assignment.field) {
		return `Assigned using ${formatFieldLabel(source, assignment.field)}.`
	}

	return "Task stays unassigned."
}

function summarizeStep(
	source: TAutomationSource | null | undefined,
	step: TAutomationDefinitionStepFormValue
) {
	if (step.type === "wait") {
		if (!step.wait_offset) {
			return `Wait offset is NOT set`
		}

		return `Waits until ${describeOffset(
			step.wait_offset,
			step.wait_mode === "relative_to_field"
				? formatFieldLabel(source, step.wait_field)
				: "this step runs",
			step.wait_mode === "relative_to_field"
		)}.`
	}

	if (step.type === "condition" && step.condition_rules) {
		return summarizeRuleGroup(step.condition_rules)
	}

	if (step.action_type === "create_trip_plan_request") {
		return `Creates a trip plan request from the ${
			source?.key === "wa_thread" ? "conversation" : "submitted form"
		}${
			trimmedString(step.trip_source)
				? ` with source "${trimmedString(step.trip_source)}"`
				: ""
		}.`
	}

	if (step.action_type === "send_whatsapp_flow") {
		return `Offers form "${step.wa_flow?.name || trimmedString(step.wa_flow_id) || "(not set)"}" with button "${
			trimmedString(step.wa_flow_cta) || "…"
		}". ${summarizeRecipients(source, step.recipients)}`
	}

	if (step.action_type === "send_whatsapp_text") {
		const body = trimmedString(step.wa_text_body)

		return `Sends "${body ? (body.length > 60 ? `${body.slice(0, 60)}…` : body) : "(not set)"}". ${summarizeRecipients(source, step.recipients)}`
	}

	if (step.action_type === "send_wa_quick_send") {
		return `Sends quick send "${step.wa_quick_send?.name || "(not set)"}". ${summarizeRecipients(source, step.recipients)}`
	}

	if (
		step.action_type === "create_task" &&
		step.task_assignment &&
		step.task_due &&
		step.task_due.offset !== undefined
	) {
		return `${summarizeTaskAssignment(source, step.task_assignment)} ${step.task_due.enabled ? `Due ${describeOffset(step.task_due.offset, step.task_due.mode === "relative_to_field" ? formatFieldLabel(source, step.task_due.field) : "this step runs", step.task_due.mode === "relative_to_field")}.` : "No due time configured."}`
	}

	return summarizeRecipients(source, step.recipients)
}

function normalizeStartType(
	source?: TAutomationSource | null,
	startType?: TAutomationStartType
): TAutomationStartType {
	if (!source) {
		return startType || "event"
	}

	if (startType && source.supported_start_types.includes(startType)) {
		return startType
	}

	return source.supported_start_types[0] || "event"
}

function normalizeEvent(
	source?: TAutomationSource | null,
	event?: string | null
) {
	if (!source?.supported_events?.length) {
		return "created"
	}

	const supportedEventKeys = source.supported_events.map((item) => item.key)

	return (
		(supportedEventKeys.includes(event || "")
			? event
			: source.supported_events[0]?.key) || "created"
	)
}

function conditionConfigFromStep(
	step?: TAutomationConditionStepConfig | null
): TAutomationRuleGroupFormValue {
	return ruleGroupFormValueFromGroup(step)
}

function stepFormValuesFromAutomation(
	automation?: TAutomation
): Array<TAutomationDefinitionStepFormValue> {
	if (!automation?.steps?.length) {
		return [createCommunicationStepFormValue(automation?.source)]
	}

	const flatten = (
		steps: Array<Required<TAutomation>["steps"][number]>,
		parentStepId: string | null = null
	): Array<TAutomationDefinitionStepFormValue> =>
		steps.flatMap((step) => {
			const localId = createId("step")
			const base =
				step.type === "wait"
					? {
							...createWaitStepFormValue(),
							id: localId,
							parent_step_id: parentStepId,
						}
					: step.type === "condition"
						? {
								...createConditionStepFormValue(),
								id: localId,
								parent_step_id: parentStepId,
							}
						: {
								...createCommunicationStepFormValue(automation.source),
								id: localId,
								parent_step_id: parentStepId,
							}

			if (step.type === "wait") {
				const config = step.config as TAutomationWaitStepConfig

				const currentStep = {
					...base,
					key: step.key,
					is_key_customized: true,
					is_key_editing: false,
					wait_mode: config.mode || "relative_to_trigger",
					wait_field: config.field || "",
					wait_offset:
						parseOffsetString(config.offset || "") ||
						createOffsetFormValue({ amount: "1", unit: "hours" }),
				}

				return [currentStep]
			}

			if (step.type === "condition") {
				const currentStep = {
					...base,
					key: step.key,
					is_key_customized: true,
					is_key_editing: false,
					condition_rules: conditionConfigFromStep(
						step.config as TAutomationConditionStepConfig
					),
				}

				return [currentStep, ...flatten(step.children || [], localId)]
			}

			const actionStep = step.config as TAutomationActionStepConfig
			const action = actionStep.action

			if (action?.type === "send_whatsapp_flow") {
				return [
					{
						...createCommunicationStepFormValue(automation.source),
						id: localId,
						parent_step_id: parentStepId,
						key: step.key,
						is_key_customized: true,
						is_key_editing: false,
						action_type: "send_whatsapp_flow",
						recipients: recipientRowsFrom(action.recipients),
						wa_flow: action.flow?.flow_id
							? {
									id: Number(action.flow.flow_id),
									name: `Flow #${action.flow.flow_id}`,
									status: "PUBLISHED",
								}
							: null,
						wa_flow_id: action.flow?.flow_id || "",
						wa_flow_cta: action.flow?.cta || "",
						wa_flow_body: action.flow?.body || "",
						wa_flow_header: action.flow?.header || "",
						wa_flow_footer: action.flow?.footer || "",
						wa_flow_screen: action.flow?.screen
							? { id: action.flow.screen, title: action.flow.screen }
							: null,
						wa_phone_number: action.flow?.phone_number_id
							? ({
									id: action.flow.phone_number_id,
									name: `Current phone #${String(action.flow.phone_number_id)}`,
									display_phone_number: `#${String(action.flow.phone_number_id)}`,
								} as TWhatsappBusinessAccountPhoneNumber)
							: null,
					},
				]
			}

			if (action?.type === "send_whatsapp_text") {
				return [
					{
						...createCommunicationStepFormValue(automation.source),
						id: localId,
						parent_step_id: parentStepId,
						key: step.key,
						is_key_customized: true,
						is_key_editing: false,
						action_type: "send_whatsapp_text",
						recipients: recipientRowsFrom(action.recipients),
						wa_text_body: action.text?.body || "",
						wa_phone_number: action.text?.phone_number_id
							? ({
									id: action.text.phone_number_id,
									name: `Current phone #${String(action.text.phone_number_id)}`,
									display_phone_number: `#${String(action.text.phone_number_id)}`,
								} as TWhatsappBusinessAccountPhoneNumber)
							: null,
					},
				]
			}

			if (action?.type === "send_wa_quick_send") {
				return [
					{
						...createCommunicationStepFormValue(automation.source),
						id: localId,
						parent_step_id: parentStepId,
						key: step.key,
						is_key_customized: true,
						is_key_editing: false,
						action_type: "send_wa_quick_send",
						recipients: recipientRowsFrom(action.recipients),
						// Only the id is stored; the picker shows the name once
						// the operator opens it.
						wa_quick_send: action.quick_send?.quick_send_id
							? {
									id: action.quick_send.quick_send_id,
									name: `Quick send #${action.quick_send.quick_send_id}`,
								}
							: null,
						wa_phone_number: action.quick_send?.phone_number_id
							? ({
									id: action.quick_send.phone_number_id,
									name: `Current phone #${String(action.quick_send.phone_number_id)}`,
									display_phone_number: `#${String(action.quick_send.phone_number_id)}`,
								} as TWhatsappBusinessAccountPhoneNumber)
							: null,
					},
				]
			}

			if (action?.type === "create_trip_plan_request") {
				return [
					{
						...createTripPlanRequestStepFormValue(),
						id: localId,
						parent_step_id: parentStepId,
						key: step.key,
						is_key_customized: true,
						is_key_editing: false,
						trip_source: action.trip_source || "",
					},
				]
			}

			if (action?.type === "create_task") {
				return [
					{
						...createTaskStepFormValue(),
						id: localId,
						parent_step_id: parentStepId,
						key: step.key,
						is_key_customized: true,
						is_key_editing: false,
						task_body_template: action.body_template || "",
						task_assignment:
							action.assign_to?.type === "static_user"
								? {
										mode: "static_user",
										user: emptyUser(action.assign_to.user_id),
										field: "",
									}
								: action.assign_to?.type === "field_path"
									? {
											mode: "field_path",
											user: null,
											field: action.assign_to.path,
										}
									: {
											mode: "none",
											user: null,
											field: "",
										},
						task_due: action.due
							? {
									enabled: true,
									mode: action.due.mode,
									field: "field" in action.due ? action.due.field : "",
									offset:
										parseOffsetString(action.due.offset) ||
										createOffsetFormValue({ amount: "1", unit: "hours" }),
								}
							: {
									enabled: false,
									mode: "relative_to_trigger",
									field: "",
									offset: createOffsetFormValue({ amount: "1", unit: "hours" }),
								},
					},
				]
			}

			const whatsappDelivery = action?.deliveries?.find(
				(delivery) => delivery.channel === "whatsapp"
			)
			const recipients = recipientRowsFrom(action?.recipients)

			return [
				{
					...createCommunicationStepFormValue(automation.source),
					id: localId,
					parent_step_id: parentStepId,
					key: step.key,
					is_key_customized: true,
					is_key_editing: false,
					recipients: recipients.length
						? recipients
						: [createRecipientRowFormValue()],
					wa_template: whatsappDelivery?.template_id
						? ({
								id: whatsappDelivery.template_id,
								name: `Current template #${String(whatsappDelivery.template_id)}`,
							} as TWATemplate)
						: null,
					wa_phone_number: whatsappDelivery?.phone_number_id
						? ({
								id: whatsappDelivery.phone_number_id,
								name: `Current phone #${String(whatsappDelivery.phone_number_id)}`,
								display_phone_number: `#${String(whatsappDelivery.phone_number_id)}`,
							} as TWhatsappBusinessAccountPhoneNumber)
						: null,
				},
			]
		})

	return flatten(automation.steps)
}

/**
 * The API's recipient buckets as form rows, for any step that messages.
 */
function recipientRowsFrom(
	recipients?: Extract<
		TAutomationActionStepConfig["action"],
		{ type: "communication" }
	>["recipients"]
): Array<TAutomationRecipientRowFormValue> {
	return [
		...(recipients?.to || []).map((recipient) =>
			createRecipientRowFormValue({
				bucket: "to",
				path: "path" in recipient ? recipient.path : "",
			})
		),
		...(recipients?.cc || []).map((recipient) =>
			createRecipientRowFormValue({
				bucket: "cc",
				path: "path" in recipient ? recipient.path : "",
			})
		),
		...(recipients?.bcc || []).map((recipient) =>
			createRecipientRowFormValue({
				bucket: "bcc",
				path: "path" in recipient ? recipient.path : "",
			})
		),
	]
}

function initialValuesFromAutomation(
	automation?: TAutomation
): TAutomationDefinitionFormValues {
	const source = automation?.source || null
	const start = automation?.start || null
	const startType = normalizeStartType(source, automation?.start_type)
	const scheduleStart =
		startType === "schedule" && start && "field" in start ? start : null
	const eventStart =
		startType === "event" && start && "event" in start ? start : null

	return {
		source,
		start_type: startType,
		event: normalizeEvent(source, eventStart?.event),
		cooldown_minutes: eventStart?.cooldown_minutes
			? String(eventStart.cooldown_minutes)
			: "",
		start_filters: start?.filters
			? ruleGroupFormValueFromGroup(start.filters)
			: createRuleGroupFormValue({}, { withInitialRule: false }),
		schedule_field:
			scheduleStart?.field ||
			source?.schedule_defaults?.field ||
			source?.schedule_fields?.[0]?.key ||
			"",
		schedule_checkpoints: (
			scheduleStart?.checkpoints ||
			source?.schedule_defaults?.checkpoints ||
			[]
		)
			.map((checkpoint) => parseCheckpointString(checkpoint))
			.filter((item): item is TAutomationCheckpointFormValue => item !== null),
		enable_interval_window: Boolean(scheduleStart?.interval_window),
		interval_start_offset:
			parseOffsetString(
				scheduleStart?.interval_window?.start_offset ||
					source?.schedule_defaults?.interval_window?.start_offset ||
					""
			) || createOffsetFormValue({ amount: "0", unit: "hours" }),
		interval_end_offset:
			parseOffsetString(
				scheduleStart?.interval_window?.end_offset ||
					source?.schedule_defaults?.interval_window?.end_offset ||
					""
			) || createOffsetFormValue({ amount: "3", unit: "days" }),
		interval_every:
			parseEveryString(
				scheduleStart?.interval_window?.every ||
					source?.schedule_defaults?.interval_window?.every ||
					""
			) || createEveryFormValue(),
		send_window: {
			from:
				scheduleStart?.send_window?.from ||
				source?.schedule_defaults?.send_window?.from ||
				"09:00",
			to:
				scheduleStart?.send_window?.to ||
				source?.schedule_defaults?.send_window?.to ||
				"21:00",
		},
		steps: normalizeStepsForKeys(
			stepFormValuesFromAutomation(automation),
			source
		),
	}
}

function buildPayload(
	values: TAutomationDefinitionFormValues
): TAutomationPayload {
	if (!values.source) {
		throw new Error("Please select a source model.")
	}

	const source = values.source
	const startType = normalizeStartType(source, values.start_type)
	const filters = values.start_filters
		? buildRuleGroup(values.start_filters, source.filter_fields)
		: undefined

	const payload: TAutomationPayload = {
		source_key: source.key,
		start_type: startType,
		start:
			startType === "event"
				? ({
						event: normalizeEvent(source, values.event),
						filters,
						...(source.supports_cooldown && Number(values.cooldown_minutes) > 0
							? { cooldown_minutes: Number(values.cooldown_minutes) }
							: {}),
					} as TAutomationEventStart)
				: ({
						field: values.schedule_field,
						checkpoints: values.schedule_checkpoints
							.map((checkpoint) => checkpointToOffset(checkpoint))
							.filter((item): item is string => Boolean(item)),
						...(values.enable_interval_window &&
						offsetToString(values.interval_start_offset) &&
						offsetToString(values.interval_end_offset) &&
						everyToString(values.interval_every)
							? {
									interval_window: {
										start_offset:
											offsetToString(values.interval_start_offset) || "",
										end_offset:
											offsetToString(values.interval_end_offset) || "",
										every: everyToString(values.interval_every) || "",
									},
								}
							: {}),
						...(values.send_window?.from && values.send_window?.to
							? {
									send_window: {
										from: values.send_window.from,
										to: values.send_window.to,
									},
								}
							: {}),
						filters,
					} as TAutomationScheduleStart),
		steps: buildNestedStepsPayload(values.steps, source),
	}

	return payload
}

function validateForm(values: TAutomationDefinitionFormValues) {
	const errors: Record<string, string> = {}
	const source = values.source
	const startType = normalizeStartType(source, values.start_type)

	if (!source) {
		errors.source = "Please select a source model."
		return errors
	}

	if (startType === "event") {
		if (!values.event) {
			errors.event = "Please choose an event."
		}

		const cooldown = trimmedString(values.cooldown_minutes)

		if (
			source.supports_cooldown &&
			cooldown !== "" &&
			!(Number.isInteger(Number(cooldown)) && Number(cooldown) >= 0)
		) {
			errors.cooldown_minutes = "Enter a whole number of minutes."
		}
	}

	if (startType === "schedule") {
		if (!values.schedule_field) {
			errors.schedule_field = "Please choose a schedule field."
		}

		const hasCheckpoints = values.schedule_checkpoints.some(
			(checkpoint) => checkpointToOffset(checkpoint) !== null
		)
		const hasInterval =
			Boolean(values.enable_interval_window) &&
			Boolean(offsetToString(values.interval_start_offset)) &&
			Boolean(offsetToString(values.interval_end_offset)) &&
			Boolean(everyToString(values.interval_every))

		if (!hasCheckpoints && !hasInterval) {
			errors.schedule_checkpoints =
				"Add one or more checkpoints or complete the repeat window."
		}

		if (
			!values.send_window?.from ||
			!values.send_window?.to ||
			values.send_window.from >= values.send_window.to
		) {
			errors["send_window.from"] =
				"Set a send window with a start before its end."
		}
	}

	validateRuleGroupFormValue(values.start_filters, "start_filters", errors)

	if (!values.steps.length) {
		errors.steps = "Add at least one step."
	}

	const seenStepKeys = new Set<string>()

	for (const [index, step] of values.steps.entries()) {
		const label = `Step ${index + 1}`
		const normalizedKey = slugifyStepKey(step.key)

		if (!normalizedKey) {
			errors[`steps[${index}].key`] = `${label}: enter a valid step key.`
		} else if (seenStepKeys.has(normalizedKey)) {
			errors[`steps[${index}].key`] = `${label}: step keys must be unique.`
		} else {
			seenStepKeys.add(normalizedKey)
		}

		if (step.type === "wait") {
			if (!offsetToString(step.wait_offset)) {
				errors[`steps[${index}].wait_offset`] = `${label}: enter a valid delay.`
			}
			if (step.wait_mode === "relative_to_field" && !step.wait_field) {
				errors[`steps[${index}].wait_field`] =
					`${label}: choose a field for the wait.`
			}
		}

		if (step.type === "condition") {
			if (
				!validateRuleGroupFormValue(
					step.condition_rules,
					`steps[${index}].condition_rules`,
					errors
				)
			) {
				errors[`steps[${index}].condition_rules`] =
					`${label}: add at least one rule.`
			}
		}

		if (step.type === "action" && step.action_type === "communication") {
			if (!step.communication_channels?.whatsapp) {
				errors[`steps[${index}].communication_channels.whatsapp`] =
					`${label}: enable WhatsApp to send messages.`
			}
			if (
				step.recipients.every((recipient) => !trimmedString(recipient.path))
			) {
				errors[`steps[${index}].recipients`] =
					`${label}: choose at least one recipient field.`
			}
			if (!step.wa_template) {
				errors[`steps[${index}].wa_template`] =
					`${label}: choose a WhatsApp template.`
			}
			// Answering a conversation, the message goes out from the number
			// the customer wrote to, so there is none to choose.
			if (!step.wa_phone_number && !source.replies_into_conversation) {
				errors[`steps[${index}].wa_phone_number`] =
					`${label}: choose a WhatsApp phone number.`
			}
		}

		if (step.type === "action" && step.action_type === "send_whatsapp_flow") {
			if (
				step.recipients.every((recipient) => !trimmedString(recipient.path))
			) {
				errors[`steps[${index}].recipients`] =
					`${label}: choose at least one recipient field.`
			}
			if (!/^\d+$/.test(trimmedString(step.wa_flow_id))) {
				errors[`steps[${index}].wa_flow`] = `${label}: pick the Flow to offer.`
			}
			if (!trimmedString(step.wa_flow_cta)) {
				errors[`steps[${index}].wa_flow_cta`] =
					`${label}: enter the button label.`
			} else if (trimmedString(step.wa_flow_cta).length > 20) {
				errors[`steps[${index}].wa_flow_cta`] =
					`${label}: the button label can be at most 20 characters.`
			}
			if (!trimmedString(step.wa_flow_body)) {
				errors[`steps[${index}].wa_flow_body`] =
					`${label}: enter the message body.`
			}
			if (!trimmedString(step.wa_flow_screen?.id)) {
				errors[`steps[${index}].wa_flow_screen`] =
					`${label}: pick the screen the form opens on.`
			}
			if (!step.wa_phone_number && !source.replies_into_conversation) {
				errors[`steps[${index}].wa_phone_number`] =
					`${label}: choose the WhatsApp number to send from.`
			}
		}

		if (step.type === "action" && step.action_type === "send_whatsapp_text") {
			if (
				step.recipients.every((recipient) => !trimmedString(recipient.path))
			) {
				errors[`steps[${index}].recipients`] =
					`${label}: choose at least one recipient field.`
			}
			if (!trimmedString(step.wa_text_body)) {
				errors[`steps[${index}].wa_text_body`] = `${label}: enter the message.`
			} else if (trimmedString(step.wa_text_body).length > 4096) {
				errors[`steps[${index}].wa_text_body`] =
					`${label}: the message can be at most 4096 characters.`
			}
			if (!step.wa_phone_number && !source.replies_into_conversation) {
				errors[`steps[${index}].wa_phone_number`] =
					`${label}: choose the WhatsApp number to send from.`
			}
		}

		if (step.type === "action" && step.action_type === "send_wa_quick_send") {
			if (
				step.recipients.every((recipient) => !trimmedString(recipient.path))
			) {
				errors[`steps[${index}].recipients`] =
					`${label}: choose at least one recipient field.`
			}
			if (!step.wa_quick_send?.id) {
				errors[`steps[${index}].wa_quick_send`] =
					`${label}: pick the quick send to send.`
			}
			if (!step.wa_phone_number && !source.replies_into_conversation) {
				errors[`steps[${index}].wa_phone_number`] =
					`${label}: choose the WhatsApp number to send from.`
			}
		}

		if (step.type === "action" && step.action_type === "create_task") {
			if (!trimmedString(step.task_body_template)) {
				errors[`steps[${index}].task_body_template`] =
					`${label}: enter the task body.`
			}
			if (
				step.task_assignment?.mode === "static_user" &&
				!step.task_assignment?.user
			) {
				errors[`steps[${index}].task_assignment.user`] =
					`${label}: choose a user to assign the task.`
			}
			if (
				step.task_assignment?.mode === "field_path" &&
				!step.task_assignment?.field
			) {
				errors[`steps[${index}].task_assignment.field`] =
					`${label}: choose a user field for assignment.`
			}
			if (
				step.task_due?.enabled &&
				!offsetToString({
					...step.task_due.offset,
					direction:
						step.task_due.mode === "relative_to_trigger"
							? "after"
							: step.task_due.offset?.direction,
				})
			) {
				errors[`steps[${index}].task_due.offset`] =
					`${label}: enter a valid due offset.`
			}
			if (
				step.task_due?.enabled &&
				step.task_due.mode === "relative_to_field" &&
				!step.task_due.field
			) {
				errors[`steps[${index}].task_due.field`] =
					`${label}: choose a due field.`
			}
		}
	}

	return errors
}

function SelectAutomationSource(
	props: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch">
) {
	const xhr = useXHR()

	const fetchSources = useCallback(
		async (
			params: Record<string, unknown>
		): Promise<IListResponse<TAutomationSource>> => {
			return XHR(xhr).getSources(params)
		},
		[xhr]
	)

	return (
		<AsyncSelect
			{...props}
			optionRenderer={({ option }) => (
				<Stack gap="1">
					<Text fontWeight="semibold">{option.name}</Text>
					{option.description ? (
						<Text color="muted" fontSize="sm">
							{option.description}
						</Text>
					) : null}
				</Stack>
			)}
			fetch={async (q) => {
				const response = await fetchSources({ q })
				return response.data
			}}
		/>
	)
}

function getTemplatePhoneNumbers(
	template?: TWATemplate | null
): TWhatsappBusinessAccountPhoneNumber[] {
	return template?.wa_business_account?.wa_phone_numbers || []
}

function SyncAutomationTemplatePhoneNumber({
	form,
	index,
	template,
}: {
	form: FormApi<TAutomationDefinitionFormValues>
	index: number
	template?: TWATemplate | null
}) {
	const phoneNumbers = getTemplatePhoneNumbers(template)

	useEffect(() => {
		const currentSteps = form.getState().values.steps || []
		const currentStep = currentSteps[index]
		const currentPhoneNumberId = String(currentStep?.wa_phone_number?.id || "")

		if (!template) {
			if (currentPhoneNumberId) {
				form.change(`steps[${index}].wa_phone_number` as never, null as never)
			}
			return
		}

		if (phoneNumbers.length === 1) {
			const onlyPhoneNumber = phoneNumbers[0]
			if (currentPhoneNumberId !== String(onlyPhoneNumber.id)) {
				form.change(
					`steps[${index}].wa_phone_number` as never,
					onlyPhoneNumber as never
				)
			}
			return
		}

		if (
			currentPhoneNumberId &&
			!phoneNumbers.some(
				(phoneNumber) => String(phoneNumber.id) === currentPhoneNumberId
			)
		) {
			form.change(`steps[${index}].wa_phone_number` as never, null as never)
		}
	}, [form, index, phoneNumbers, template])

	return null
}

export function AutomationDefinitionForm({
	automation,
	onSuccess,
	onCancel,
}: {
	automation?: TAutomation
	onSuccess: (automation: TAutomation) => void
	onCancel: () => void
}) {
	const xhr = useXHR()
	const [initialValues] = useState<TAutomationDefinitionFormValues>(() =>
		initialValuesFromAutomation(automation)
	)
	const [selectedSource, setSelectedSource] =
		useState<TAutomationSource | null>(automation?.source || null)

	return (
		<Form<TAutomationDefinitionFormValues>
			initialValues={initialValues}
			validate={validateForm}
			subscription={{ submitting: true }}
			onSubmit={withServerErrors(async (values) => {
				const payload = buildPayload(values)
				const response = automation
					? await XHR(xhr).update(automation.id, payload)
					: await XHR(xhr).create(payload)

				showSnackbar(
					automation
						? "Automation updated successfully."
						: "Automation created successfully."
				)
				onSuccess(response.data)
			})}
			mutators={{ ...arrayMutators }}
		>
			{({ handleSubmit, submitting, form }) => {
				const source = selectedSource
				const sourceFilterFields = source?.filter_fields || []
				const sourceWaitFields = source?.wait_fields || []
				const sourceScheduleFields = source?.schedule_fields || []
				const sourceRecipientFields = source?.recipient_fields || []
				const sourceAssignableUserFields = source?.assignable_user_fields || []
				const supportedChannels = source?.supported_channels || ["whatsapp"]
				const supportedStartTypes = source?.supported_start_types || [
					"event",
					"schedule",
				]

				const updateSteps = (
					updater: (
						steps: Array<TAutomationDefinitionStepFormValue>
					) => Array<TAutomationDefinitionStepFormValue>
				) => {
					const currentSteps =
						(form.getState().values.steps as
							| Array<TAutomationDefinitionStepFormValue>
							| undefined) || []
					form.change(
						"steps",
						normalizeStepsForKeys(updater(currentSteps), source)
					)
				}

				return (
					<form noValidate onSubmit={handleSubmit}>
						<Stack gap="6">
							<Alert status="info" hideIcon>
								<Stack gap="2">
									<Text fontSize="sm" fontWeight="semibold">
										Automation Engine Mapping
									</Text>
									<Inline gap="2" flexWrap="wrap">
										<Badge>When</Badge>
										<Text color="muted">→</Text>
										<Badge>Source + Start + Filters</Badge>
										<Text color="muted">→</Text>
										<Badge>Then</Badge>
										<Text color="muted">→</Text>
										<Badge>Ordered Steps</Badge>
									</Inline>
									<Text fontSize="sm" color="muted">
										This builder maps directly to the backend model:
										source-driven starts, rule filters, and typed automation
										steps.
									</Text>
								</Stack>
							</Alert>

							<AutomationFormSection
								title="When"
								description="Choose the source model and define whether the automation starts from an event or a date-based schedule."
							>
								<Grid>
									<Col sm={12} md={7} paddingX="0" paddingY="2">
										<SelectField
											name="source"
											label="Source Model"
											select={SelectAutomationSource}
											placeholder="Select a source"
											required
											fetchOnMount
											onChange={(nextSource: TAutomationSource | null) => {
												setSelectedSource(nextSource)
												form.change("source", nextSource)
												form.change(
													"start_type",
													normalizeStartType(nextSource)
												)
												form.change("event", normalizeEvent(nextSource))
												form.change(
													"schedule_field",
													nextSource?.schedule_defaults?.field ||
														nextSource?.schedule_fields?.[0]?.key ||
														""
												)
												form.change(
													"schedule_checkpoints",
													(nextSource?.schedule_defaults?.checkpoints || [])
														.map((checkpoint) =>
															parseCheckpointString(checkpoint)
														)
														.filter(
															(item): item is TAutomationCheckpointFormValue =>
																item !== null
														)
												)
												form.change(
													"interval_start_offset",
													parseOffsetString(
														nextSource?.schedule_defaults?.interval_window
															?.start_offset || ""
													) ||
														createOffsetFormValue({
															amount: "0",
															unit: "hours",
														})
												)
												form.change(
													"interval_end_offset",
													parseOffsetString(
														nextSource?.schedule_defaults?.interval_window
															?.end_offset || ""
													) ||
														createOffsetFormValue({
															amount: "3",
															unit: "days",
														})
												)
												form.change(
													"interval_every",
													parseEveryString(
														nextSource?.schedule_defaults?.interval_window
															?.every || ""
													) || createEveryFormValue()
												)
												const currentSteps =
													(form.getState().values.steps as
														| Array<TAutomationDefinitionStepFormValue>
														| undefined) || []
												form.change(
													"steps",
													normalizeStepsForKeys(
														currentSteps.map((step) =>
															step.type === "action" &&
															step.action_type === "communication" &&
															step.recipients.every(
																(recipient) => !recipient.path
															)
																? {
																		...step,
																		recipients: [
																			createRecipientRowFormValue({
																				path:
																					nextSource?.recipient_fields.find(
																						(field) =>
																							field.recipient_types?.includes(
																								"phone"
																							)
																					)?.key || "",
																			}),
																		],
																	}
																: step
														),
														nextSource
													)
												)
											}}
										/>
									</Col>
									<Col sm={12} md={5} paddingX="0" paddingY="2">
										<GetFieldValue<TAutomationStartType> name="start_type">
											{({ value: startType }) =>
												supportedStartTypes.length > 1 ? (
													<SelectInputField
														name="start_type"
														label="Start Type"
													>
														{supportedStartTypes.map((item) => (
															<option key={item} value={item}>
																{item === "schedule" ? "Schedule" : "Event"}
															</option>
														))}
													</SelectInputField>
												) : (
													<Stack gap="1">
														<Text fontWeight="semibold">Start Type</Text>
														<Text>
															{(startType || supportedStartTypes[0]) ===
															"schedule"
																? "Schedule"
																: "Event"}
														</Text>
													</Stack>
												)
											}
										</GetFieldValue>
									</Col>
								</Grid>

								{source ? (
									<Alert status="info" hideIcon>
										<Stack gap="1">
											<Text fontWeight="semibold">{source.name}</Text>
											<Text fontSize="sm" color="muted">
												{source.description}
											</Text>
										</Stack>
									</Alert>
								) : null}

								<GetFieldValue<TAutomationStartType> name="start_type">
									{({ value: startType }) =>
										startType === "event" ? (
											<Stack gap="4">
												<SelectInputField name="event" label="Event">
													{(source?.supported_events || []).map((event) => (
														<option key={event.key} value={event.key}>
															{event.label}
														</option>
													))}
												</SelectInputField>
												{source?.supports_cooldown ? (
													<TextInputField
														name="cooldown_minutes"
														label="Cooldown (minutes)"
														type="number"
														min={0}
														placeholder="0"
														help="After it runs, stay quiet in the same conversation for this long. Leave empty to run every time."
													/>
												) : null}
											</Stack>
										) : (
											<Stack gap="4">
												<SelectInputField
													name="schedule_field"
													label="Schedule Field"
												>
													<option value="">Select a field</option>
													{sourceScheduleFields.map((field) => (
														<option key={field.key} value={field.key}>
															{formatFieldOptionLabel(field)}
														</option>
													))}
												</SelectInputField>

												<GetFieldValue<string> name="schedule_field">
													{({ value: scheduleField }) => (
														<Stack gap="3">
															<Inline
																justifyContent="between"
																alignItems="center"
																flexWrap="wrap"
																gap="2"
															>
																<Stack gap="1">
																	<Text fontWeight="semibold">Checkpoints</Text>
																	<Text color="muted" fontSize="sm">
																		Each checkpoint creates one independent run
																		{formatFieldLabel(source, scheduleField)
																			? ` relative to ${formatFieldLabel(
																					source,
																					scheduleField
																				)}`
																			: ""}
																		.
																	</Text>
																</Stack>
															</Inline>
															<CheckpointsField
																name="schedule_checkpoints"
																previewLabel={formatFieldLabel(
																	source,
																	scheduleField
																)}
															/>
														</Stack>
													)}
												</GetFieldValue>
												<GetFieldValue<string> name="schedule_field">
													{({ value: scheduleField }) => (
														<Stack gap="3">
															<Inline
																justifyContent="between"
																alignItems="center"
																flexWrap="wrap"
																gap="2"
															>
																<Stack gap="1">
																	<Text fontWeight="semibold">
																		Repeat Window
																	</Text>
																	<Text color="muted" fontSize="sm">
																		Optional. Use this only when you want
																		repeated runs between two offsets.
																	</Text>
																</Stack>
															</Inline>
															<IntervalWindowField
																previewLabel={formatFieldLabel(
																	source,
																	scheduleField
																)}
															/>
														</Stack>
													)}
												</GetFieldValue>
												<Stack gap="3">
													<Stack gap="1">
														<Text fontWeight="semibold">Send Window</Text>
														<Text color="muted" fontSize="sm">
															Local hours reminders may go out; anything outside
															moves to the next window open (default
															09:00–21:00).
														</Text>
													</Stack>
													<SendWindowField />
												</Stack>
											</Stack>
										)
									}
								</GetFieldValue>
							</AutomationFormSection>

							<AutomationFormSection
								title="Start Filters"
								description="Add nested AND/OR rules to decide whether this automation should start for a matching record."
							>
								<GetFieldValue<TAutomationRuleGroupFormValue> name="start_filters">
									{({ value: group }) => (
										<RuleGroupEditor
											group={
												group ||
												createRuleGroupFormValue({}, { withInitialRule: false })
											}
											source={source}
											fields={sourceFilterFields}
											prefix="start_filters"
											allowEmpty
											onChange={(nextGroup) =>
												form.change("start_filters", nextGroup)
											}
										/>
									)}
								</GetFieldValue>
							</AutomationFormSection>

							<AutomationFormSection
								title="Then"
								description="Add the ordered steps that should run after the automation starts."
							>
								<GetFieldValue<
									Array<TAutomationDefinitionStepFormValue>
								> name="steps">
									{({ value: steps }) => (
										<Stack gap="4">
											{(steps || []).map((step, index) => {
												const depth = stepDepth(steps || [], step)

												return (
													<Box
														key={step.id}
														borderWidth="1"
														rounded="md"
														padding="4"
														marginLeft={depth ? "6" : undefined}
													>
														<Stack gap="4">
															<Inline
																justifyContent="between"
																alignItems="center"
																flexWrap="wrap"
																gap="2"
															>
																<Inline gap="2" alignItems="center">
																	<Badge>Step {index + 1}</Badge>
																	<Badge>{step.key || "step_key"}</Badge>
																	{step.parent_step_id ? (
																		<Badge>Nested</Badge>
																	) : null}
																	<Badge>
																		{step.type === "action"
																			? step.action_type === "communication"
																				? "Communication"
																				: step.action_type ===
																					  "create_trip_plan_request"
																					? "Create Trip Plan Request"
																					: step.action_type ===
																						  "send_whatsapp_flow"
																						? "Offer WhatsApp Form"
																						: step.action_type ===
																							  "send_wa_quick_send"
																							? "Send Quick Send"
																							: step.action_type ===
																								  "send_whatsapp_text"
																								? "Send WhatsApp Message"
																								: "Create Task"
																			: step.type === "wait"
																				? "Wait"
																				: "Condition"}
																	</Badge>
																</Inline>
																<Text color="muted" fontSize="sm">
																	{summarizeStep(source, step)}
																</Text>
																<Inline gap="2">
																	<Button
																		type="button"
																		level="tertiary"
																		disabled={index === 0}
																		onClick={() =>
																			updateSteps((currentSteps) => {
																				const next = [...currentSteps]
																				;[next[index - 1], next[index]] = [
																					next[index],
																					next[index - 1],
																				]
																				return next
																			})
																		}
																	>
																		Up
																	</Button>
																	<Button
																		type="button"
																		level="tertiary"
																		disabled={
																			index === (steps || []).length - 1
																		}
																		onClick={() =>
																			updateSteps((currentSteps) => {
																				const next = [...currentSteps]
																				;[next[index + 1], next[index]] = [
																					next[index],
																					next[index + 1],
																				]
																				return next
																			})
																		}
																	>
																		Down
																	</Button>
																	<Button
																		type="button"
																		level="tertiary"
																		disabled={(steps || []).length === 1}
																		onClick={() =>
																			updateSteps((currentSteps) =>
																				removeStepTree(currentSteps, step.id)
																			)
																		}
																	>
																		<Icons.Trash />
																	</Button>
																</Inline>
															</Inline>

															<Grid>
																<Col
																	sm={12}
																	md={step.is_key_editing ? 10 : 12}
																	paddingX="0"
																	paddingY="2"
																>
																	{step.is_key_editing ? (
																		<TextInputField
																			name={`steps[${index}].key`}
																			label="Step Key"
																			placeholder="send_whatsapp_message"
																			onChange={(event) =>
																				updateSteps((currentSteps) =>
																					currentSteps.map(
																						(currentStep, currentIndex) =>
																							currentIndex === index
																								? {
																										...currentStep,
																										key: slugifyStepKey(
																											event.currentTarget.value
																										),
																										is_key_customized:
																											event.currentTarget.value.trim() !==
																											"",
																									}
																								: currentStep
																					)
																				)
																			}
																		/>
																	) : (
																		<Stack gap="2">
																			<Text color="muted" fontSize="sm">
																				Step Key
																			</Text>
																			<Inline
																				alignItems="center"
																				gap="2"
																				flexWrap="wrap"
																			>
																				<Text>{step.key || "step"}</Text>
																				<Button
																					type="button"
																					level="tertiary"
																					onClick={() =>
																						updateSteps((currentSteps) =>
																							currentSteps.map(
																								(currentStep, currentIndex) =>
																									currentIndex === index
																										? {
																												...currentStep,
																												is_key_editing: true,
																											}
																										: currentStep
																							)
																						)
																					}
																				>
																					<Icons.Pencil />
																				</Button>
																			</Inline>
																		</Stack>
																	)}
																</Col>
																{step.is_key_editing ? (
																	<Col sm={12} md={2} paddingX="0" paddingY="2">
																		<Stack gap="2">
																			<Button
																				type="button"
																				level="tertiary"
																				onClick={() =>
																					updateSteps((currentSteps) =>
																						currentSteps.map(
																							(currentStep, currentIndex) =>
																								currentIndex === index
																									? {
																											...currentStep,
																											is_key_editing: false,
																										}
																									: currentStep
																						)
																					)
																				}
																			>
																				Done
																			</Button>
																			<Button
																				type="button"
																				level="tertiary"
																				onClick={() =>
																					updateSteps((currentSteps) =>
																						currentSteps.map(
																							(currentStep, currentIndex) =>
																								currentIndex === index
																									? {
																											...currentStep,
																											key: "",
																											is_key_customized: false,
																										}
																									: currentStep
																						)
																					)
																				}
																			>
																				Reset Key
																			</Button>
																		</Stack>
																	</Col>
																) : null}
															</Grid>
															<Text color="muted" fontSize="sm">
																This key is used to reference this step in later
																conditions, for example
																{` steps.${step.key || "send_intro"}.status`}.
															</Text>

															{step.type === "wait" ? (
																<Stack gap="3">
																	<SelectInputField
																		name={`steps[${index}].wait_mode`}
																		label="Wait Mode"
																		onChange={(event) =>
																			updateSteps((currentSteps) =>
																				currentSteps.map(
																					(currentStep, currentIndex) =>
																						currentIndex === index
																							? {
																									...currentStep,
																									wait_mode: event.currentTarget
																										.value as TAutomationDefinitionStepFormValue["wait_mode"],
																								}
																							: currentStep
																				)
																			)
																		}
																	>
																		<option value="relative_to_trigger">
																			Delay from this step
																		</option>
																		<option value="relative_to_field">
																			Relative to field
																		</option>
																	</SelectInputField>
																	<GetFieldValue<
																		"relative_to_trigger" | "relative_to_field"
																	>
																		name={`steps[${index}].wait_mode`}
																	>
																		{({ value: waitMode }) => (
																			<>
																				{waitMode === "relative_to_field" ? (
																					<SelectInputField
																						name={`steps[${index}].wait_field`}
																						label="Field"
																						onChange={(event) =>
																							updateSteps((currentSteps) =>
																								currentSteps.map(
																									(
																										currentStep,
																										currentIndex
																									) =>
																										currentIndex === index
																											? {
																													...currentStep,
																													wait_field:
																														event.currentTarget
																															.value,
																												}
																											: currentStep
																								)
																							)
																						}
																					>
																						<option value="">
																							Select a field
																						</option>
																						{sourceWaitFields.map((field) => (
																							<option
																								key={field.key}
																								value={field.key}
																							>
																								{formatFieldOptionLabel(field)}
																							</option>
																						))}
																					</SelectInputField>
																				) : null}
																				<Grid>
																					{waitMode === "relative_to_field" ? (
																						<Col
																							sm={12}
																							md={4}
																							paddingX="0"
																							paddingY="2"
																						>
																							<SelectInputField
																								name={`steps[${index}].wait_offset.direction`}
																							>
																								<option value="before">
																									Before
																								</option>
																								<option value="after">
																									After
																								</option>
																							</SelectInputField>
																						</Col>
																					) : null}
																					<Col
																						sm={12}
																						md={
																							waitMode === "relative_to_field"
																								? 4
																								: 6
																						}
																						paddingX="0"
																						paddingY="2"
																					>
																						<TextInputField
																							name={`steps[${index}].wait_offset.amount`}
																							type="number"
																							label={
																								waitMode === "relative_to_field"
																									? "Amount"
																									: "Delay"
																							}
																						/>
																					</Col>
																					<Col
																						sm={12}
																						md={
																							waitMode === "relative_to_field"
																								? 4
																								: 6
																						}
																						paddingX="0"
																						paddingY="2"
																					>
																						<SelectInputField
																							name={`steps[${index}].wait_offset.unit`}
																						>
																							<option value="minutes">
																								Minutes
																							</option>
																							<option value="hours">
																								Hours
																							</option>
																							<option value="days">Days</option>
																						</SelectInputField>
																					</Col>
																				</Grid>
																				<GetFieldValue<TAutomationOffsetFormValue>
																					name={`steps[${index}].wait_offset`}
																				>
																					{({ value: offset }) => (
																						<GetFieldValue<string>
																							name={`steps[${index}].wait_field`}
																						>
																							{({ value: waitField }) => (
																								<Text
																									color="muted"
																									fontSize="sm"
																								>
																									Waits until{" "}
																									{describeOffset(
																										offset,
																										waitMode ===
																											"relative_to_field"
																											? formatFieldLabel(
																													source,
																													waitField
																												)
																											: "this step runs",
																										waitMode ===
																											"relative_to_field"
																									)}
																									.
																								</Text>
																							)}
																						</GetFieldValue>
																					)}
																				</GetFieldValue>
																			</>
																		)}
																	</GetFieldValue>
																</Stack>
															) : null}

															{step.type === "condition" ? (
																<Stack gap="3">
																	<GetFieldValue<TAutomationRuleGroupFormValue>
																		name={`steps[${index}].condition_rules`}
																	>
																		{({ value: group }) => (
																			<RuleGroupEditor
																				group={
																					group || createRuleGroupFormValue()
																				}
																				source={source}
																				fields={sourceFilterFields}
																				availableSteps={(steps || []).slice(
																					0,
																					index
																				)}
																				allowStepReferences
																				prefix={`steps[${index}].condition_rules`}
																				onChange={(nextGroup) =>
																					form.change(
																						`steps[${index}].condition_rules` as never,
																						nextGroup as never
																					)
																				}
																			/>
																		)}
																	</GetFieldValue>
																	<Inline gap="2" flexWrap="wrap">
																		<Button
																			type="button"
																			level="secondary"
																			onClick={() =>
																				updateSteps((currentSteps) =>
																					insertStepInsideCondition(
																						currentSteps,
																						step.id,
																						createCommunicationStepFormValue(
																							source
																						)
																					)
																				)
																			}
																		>
																			<Icons.Plus /> Add Step Inside Condition
																		</Button>
																		<Button
																			type="button"
																			level="tertiary"
																			onClick={() =>
																				updateSteps((currentSteps) =>
																					insertStepInsideCondition(
																						currentSteps,
																						step.id,
																						createTaskStepFormValue()
																					)
																				)
																			}
																		>
																			Add Task Inside Condition
																		</Button>
																		<Button
																			type="button"
																			level="tertiary"
																			onClick={() =>
																				updateSteps((currentSteps) =>
																					insertStepInsideCondition(
																						currentSteps,
																						step.id,
																						createWaitStepFormValue()
																					)
																				)
																			}
																		>
																			Add Wait Inside Condition
																		</Button>
																		<Button
																			type="button"
																			level="tertiary"
																			onClick={() =>
																				updateSteps((currentSteps) =>
																					insertStepInsideCondition(
																						currentSteps,
																						step.id,
																						createConditionStepFormValue()
																					)
																				)
																			}
																		>
																			<Icons.Sliders /> Add Nested Condition
																		</Button>
																	</Inline>
																	<Text color="muted" fontSize="sm">
																		If these rules match, the nested steps
																		immediately below this card will run. If
																		they do not match, those nested steps will
																		be skipped and the automation will continue
																		with the next sibling step.
																	</Text>
																</Stack>
															) : null}

															{step.type === "action" ? (
																<Stack gap="4">
																	<SelectInputField
																		name={`steps[${index}].action_type`}
																		label="Action Type"
																		onChange={(event) =>
																			updateSteps((currentSteps) =>
																				currentSteps.map(
																					(currentStep, currentIndex) =>
																						currentIndex === index
																							? {
																									...currentStep,
																									action_type: event
																										.currentTarget
																										.value as TAutomationActionType,
																								}
																							: currentStep
																				)
																			)
																		}
																	>
																		{source?.supported_actions?.includes(
																			"communication"
																		) ? (
																			<option value="communication">
																				Communication
																			</option>
																		) : null}
																		{source?.supported_actions?.includes(
																			"create_task"
																		) ? (
																			<option value="create_task">
																				Create Task
																			</option>
																		) : null}
																		{source?.supported_actions?.includes(
																			"send_whatsapp_flow"
																		) ? (
																			<option value="send_whatsapp_flow">
																				Offer WhatsApp Form
																			</option>
																		) : null}
																		{source?.supported_actions?.includes(
																			"send_whatsapp_text"
																		) ? (
																			<option value="send_whatsapp_text">
																				Send WhatsApp Message
																			</option>
																		) : null}
																		{source?.supported_actions?.includes(
																			"send_wa_quick_send"
																		) ? (
																			<option value="send_wa_quick_send">
																				Send Quick Send
																			</option>
																		) : null}
																		{source?.supported_actions?.includes(
																			"create_trip_plan_request"
																		) ? (
																			<option value="create_trip_plan_request">
																				Create Trip Plan Request
																			</option>
																		) : null}
																	</SelectInputField>

																	<GetFieldValue<TAutomationActionType>
																		name={`steps[${index}].action_type`}
																	>
																		{({ value: actionType }) =>
																			actionType ===
																			"create_trip_plan_request" ? (
																				<Stack gap="3">
																					<Text color="muted" fontSize="sm">
																						Turns the submitted form into a trip
																						plan request. Answers are matched to
																						the enquiry by the field names used
																						in the Flow (destination,
																						start_date, no_of_days,
																						no_of_adults, no_of_children,
																						comments); anything else is kept as
																						an extra field. The customer's
																						number and name come from the
																						conversation, which is linked to the
																						enquiry.
																					</Text>
																					<TextInputField
																						name={`steps[${index}].trip_source`}
																						label="Enquiry Source"
																						placeholder="WhatsApp"
																						help="Recorded as the enquiry's source. Leave blank for WhatsApp."
																					/>
																				</Stack>
																			) : actionType === "create_task" ? (
																				<Stack gap="4">
																					<TextAreaInputField
																						name={`steps[${index}].task_body_template`}
																						label="Task Body"
																						placeholder="Follow up on {{customer_name}}."
																					/>
																					<SelectInputField
																						name={`steps[${index}].task_assignment.mode`}
																						label="Assign To"
																					>
																						<option value="none">
																							Leave unassigned
																						</option>
																						<option value="static_user">
																							Specific user
																						</option>
																						<option value="field_path">
																							User field
																						</option>
																					</SelectInputField>
																					<GetFieldValue<
																						TAutomationTaskAssignmentFormValue["mode"]
																					>
																						name={`steps[${index}].task_assignment.mode`}
																					>
																						{({ value: assignmentMode }) => (
																							<>
																								{assignmentMode ===
																								"static_user" ? (
																									<SelectField
																										select={SelectUsers}
																										name={`steps[${index}].task_assignment.user`}
																										label="User"
																										fetchOnMount
																										multiple={false}
																									/>
																								) : null}
																								{assignmentMode ===
																								"field_path" ? (
																									<SelectInputField
																										name={`steps[${index}].task_assignment.field`}
																										label="User Field"
																									>
																										<option value="">
																											Select a field
																										</option>
																										{sourceAssignableUserFields.map(
																											(field) => (
																												<option
																													key={field.key}
																													value={field.key}
																												>
																													{formatFieldOptionLabel(
																														field
																													)}
																												</option>
																											)
																										)}
																									</SelectInputField>
																								) : null}
																							</>
																						)}
																					</GetFieldValue>
																					{step.task_assignment ? (
																						<Text color="muted" fontSize="sm">
																							{summarizeTaskAssignment(
																								source,
																								step.task_assignment
																							)}
																						</Text>
																					) : null}
																					<CheckboxInputField
																						name={`steps[${index}].task_due.enabled`}
																						label="Set due time"
																					/>
																					<GetFieldValue<boolean>
																						name={`steps[${index}].task_due.enabled`}
																					>
																						{({ value: enabled }) =>
																							enabled ? (
																								<Stack gap="3">
																									<SelectInputField
																										name={`steps[${index}].task_due.mode`}
																										label="Due Mode"
																									>
																										<option value="relative_to_trigger">
																											Delay from this step
																										</option>
																										<option value="relative_to_field">
																											Relative to field
																										</option>
																									</SelectInputField>
																									<GetFieldValue<
																										| "relative_to_trigger"
																										| "relative_to_field"
																									>
																										name={`steps[${index}].task_due.mode`}
																									>
																										{({ value: dueMode }) => (
																											<>
																												{dueMode ===
																												"relative_to_field" ? (
																													<SelectInputField
																														name={`steps[${index}].task_due.field`}
																														label="Due Field"
																													>
																														<option value="">
																															Select a field
																														</option>
																														{sourceWaitFields.map(
																															(field) => (
																																<option
																																	key={
																																		field.key
																																	}
																																	value={
																																		field.key
																																	}
																																>
																																	{formatFieldOptionLabel(
																																		field
																																	)}
																																</option>
																															)
																														)}
																													</SelectInputField>
																												) : null}
																												<Grid>
																													{dueMode ===
																													"relative_to_field" ? (
																														<Col
																															sm={12}
																															md={4}
																															paddingX="0"
																															paddingY="2"
																														>
																															<SelectInputField
																																name={`steps[${index}].task_due.offset.direction`}
																															>
																																<option value="before">
																																	Before
																																</option>
																																<option value="after">
																																	After
																																</option>
																															</SelectInputField>
																														</Col>
																													) : null}
																													<Col
																														sm={12}
																														md={
																															dueMode ===
																															"relative_to_field"
																																? 4
																																: 6
																														}
																														paddingX="0"
																														paddingY="2"
																													>
																														<TextInputField
																															name={`steps[${index}].task_due.offset.amount`}
																															type="number"
																														/>
																													</Col>
																													<Col
																														sm={12}
																														md={
																															dueMode ===
																															"relative_to_field"
																																? 4
																																: 6
																														}
																														paddingX="0"
																														paddingY="2"
																													>
																														<SelectInputField
																															name={`steps[${index}].task_due.offset.unit`}
																														>
																															<option value="minutes">
																																Minutes
																															</option>
																															<option value="hours">
																																Hours
																															</option>
																															<option value="days">
																																Days
																															</option>
																														</SelectInputField>
																													</Col>
																												</Grid>
																											</>
																										)}
																									</GetFieldValue>
																								</Stack>
																							) : null
																						}
																					</GetFieldValue>
																				</Stack>
																			) : (
																				<Stack gap="4">
																					{actionType === "communication" ? (
																						<Stack gap="2">
																							<Text fontWeight="semibold">
																								Communication Channels
																							</Text>
																							<Inline gap="3" flexWrap="wrap">
																								<CheckboxInputField
																									name={`steps[${index}].communication_channels.whatsapp`}
																									label="WhatsApp"
																									onChange={(event) =>
																										updateSteps(
																											(currentSteps) =>
																												currentSteps.map(
																													(
																														currentStep,
																														currentIndex
																													) =>
																														currentIndex ===
																														index
																															? ({
																																	...currentStep,
																																	communication_channels:
																																		{
																																			...currentStep.communication_channels,
																																			whatsapp:
																																				event
																																					.currentTarget
																																					.checked,
																																		},
																																} as typeof currentStep)
																															: currentStep
																												)
																										)
																									}
																								/>
																								<CheckboxInputField
																									name={`steps[${index}].communication_channels.email`}
																									label="Email (coming soon)"
																									disabled
																								/>
																							</Inline>
																							{!supportedChannels.includes(
																								"email"
																							) ? (
																								<Text
																									color="muted"
																									fontSize="sm"
																								>
																									Email is not executable yet.
																									WhatsApp is the first
																									supported delivery channel.
																								</Text>
																							) : null}
																						</Stack>
																					) : null}

																					<Stack gap="3">
																						<Inline
																							justifyContent="between"
																							alignItems="center"
																							gap="2"
																						>
																							<Stack gap="1">
																								<Text fontWeight="semibold">
																									Recipients
																								</Text>
																								<Text
																									color="muted"
																									fontSize="sm"
																								>
																									Pick recipient fields from the
																									selected source model.
																								</Text>
																							</Stack>
																							<Button
																								type="button"
																								level="secondary"
																								onClick={() =>
																									form.change(
																										`steps[${index}].recipients` as never,
																										[
																											...((
																												form.getState().values
																													.steps?.[
																													index
																												] as TAutomationDefinitionStepFormValue
																											)?.recipients || []),
																											createRecipientRowFormValue(),
																										] as never
																									)
																								}
																							>
																								<Icons.Plus /> Add Recipient
																							</Button>
																						</Inline>
																						<GetFieldValue<
																							Array<TAutomationRecipientRowFormValue>
																						>
																							name={`steps[${index}].recipients`}
																						>
																							{({ value: recipients }) => (
																								<Stack gap="3">
																									<Text
																										color="muted"
																										fontSize="sm"
																									>
																										{summarizeRecipients(
																											source,
																											recipients || []
																										)}
																									</Text>
																									{(recipients || []).map(
																										(
																											recipient,
																											recipientIndex
																										) => (
																											<Box
																												key={recipient.id}
																												borderWidth="1"
																												rounded="md"
																												padding="3"
																											>
																												<Grid>
																													<Col
																														sm={12}
																														md={3}
																														paddingX="0"
																														paddingY="2"
																													>
																														<SelectInputField
																															name={`steps[${index}].recipients[${recipientIndex}].bucket`}
																															label="Bucket"
																														>
																															<option value="to">
																																To
																															</option>
																															<option value="cc">
																																Cc
																															</option>
																															<option value="bcc">
																																Bcc
																															</option>
																														</SelectInputField>
																													</Col>
																													<Col
																														sm={12}
																														md={7}
																														paddingX="0"
																														paddingY="2"
																													>
																														<SelectInputField
																															name={`steps[${index}].recipients[${recipientIndex}].path`}
																															label="Recipient Field"
																														>
																															<option value="">
																																Select a field
																															</option>
																															{sourceRecipientFields
																																.filter(
																																	(field) =>
																																		field.recipient_types?.includes(
																																			"phone"
																																		)
																																)
																																.map(
																																	(field) => (
																																		<option
																																			key={
																																				field.key
																																			}
																																			value={
																																				field.key
																																			}
																																		>
																																			{formatFieldOptionLabel(
																																				field
																																			)}
																																		</option>
																																	)
																																)}
																														</SelectInputField>
																													</Col>
																													<Col
																														sm={12}
																														md={2}
																														paddingX="0"
																														paddingY="2"
																													>
																														<Button
																															type="button"
																															level="tertiary"
																															onClick={() =>
																																form.change(
																																	`steps[${index}].recipients` as never,
																																	(
																																		recipients ||
																																		[]
																																	).filter(
																																		(item) =>
																																			item.id !==
																																			recipient.id
																																	) as never
																																)
																															}
																														>
																															<Icons.Trash />
																														</Button>
																													</Col>
																												</Grid>
																											</Box>
																										)
																									)}
																								</Stack>
																							)}
																						</GetFieldValue>
																					</Stack>

																					{actionType ===
																					"send_whatsapp_text" ? (
																						<Stack gap="3">
																							<Text fontWeight="semibold">
																								WhatsApp Message
																							</Text>
																							<Text color="muted" fontSize="sm">
																								Sends a message you write. Needs
																								no template approval, but can
																								only be delivered within 24
																								hours of the customer's last
																								message.
																							</Text>
																							<TextAreaInputField
																								name={`steps[${index}].wa_text_body`}
																								label="Message"
																								placeholder="Thanks {{customer_name}}, how can we help?"
																								maxLength={4096}
																								help="Placeholders resolve from the source's variables."
																							/>
																							{source?.replies_into_conversation ? (
																								<Text
																									color="muted"
																									fontSize="sm"
																								>
																									Sent from the number the
																									customer wrote to.
																								</Text>
																							) : (
																								<SelectField
																									select={SelectWAPhoneNumber}
																									name={`steps[${index}].wa_phone_number`}
																									label="Send From"
																									placeholder="Select WhatsApp phone number"
																									allowAll
																								/>
																							)}
																						</Stack>
																					) : actionType ===
																					  "send_wa_quick_send" ? (
																						<Stack gap="3">
																							<Text fontWeight="semibold">
																								Quick Send
																							</Text>
																							<Text color="muted" fontSize="sm">
																								Sends one of your saved quick
																								sends, its messages in order.
																								Needs no template approval, but
																								can only be delivered within 24
																								hours of the customer's last
																								message.
																							</Text>
																							<SelectField
																								select={SelectWAQuickSend}
																								name={`steps[${index}].wa_quick_send`}
																								label="Quick Send"
																								placeholder="Select a quick send"
																							/>
																							{source?.replies_into_conversation ? (
																								<Text
																									color="muted"
																									fontSize="sm"
																								>
																									Sent from the number the
																									customer wrote to.
																								</Text>
																							) : (
																								<SelectField
																									select={SelectWAPhoneNumber}
																									name={`steps[${index}].wa_phone_number`}
																									label="Send From"
																									placeholder="Select WhatsApp phone number"
																									allowAll
																								/>
																							)}
																						</Stack>
																					) : actionType ===
																					  "send_whatsapp_flow" ? (
																						<Stack gap="3">
																							<Text fontWeight="semibold">
																								WhatsApp Form
																							</Text>
																							<Text color="muted" fontSize="sm">
																								Offers a published Flow as a
																								free-form bubble with one button
																								that opens the form. Needs no
																								template approval, but can only
																								be delivered within 24 hours of
																								the customer's last message.
																							</Text>
																							<SelectField
																								select={SelectWAFlow}
																								name={`steps[${index}].wa_flow`}
																								label="Flow"
																								placeholder="Select a published Flow"
																								help="Read live from your WhatsApp Business Accounts. Flows are authored in Meta's Flow Builder."
																								onChange={(value: unknown) => {
																									const flow = (
																										Array.isArray(value)
																											? value[0]
																											: value
																									) as TWAFlow | null
																									form.change(
																										`steps[${index}].wa_flow` as never,
																										(flow ?? null) as never
																									)
																									form.change(
																										`steps[${index}].wa_flow_id` as never,
																										String(
																											flow?.id ?? ""
																										) as never
																									)
																									// A screen belongs to one Flow;
																									// the screen picker selects the
																									// new Flow's first.
																									form.change(
																										`steps[${index}].wa_flow_screen` as never,
																										null as never
																									)
																								}}
																							/>
																							<TextInputField
																								name={`steps[${index}].wa_flow_cta`}
																								label="Button Label"
																								placeholder="Plan my trip"
																								maxLength={20}
																							/>
																							<TextAreaInputField
																								name={`steps[${index}].wa_flow_body`}
																								label="Message"
																								placeholder="Tell us about your trip, {{customer_name}}."
																								help="Placeholders resolve from the source's variables."
																							/>
																							<Grid gap="3">
																								<Col>
																									<TextInputField
																										name={`steps[${index}].wa_flow_header`}
																										label="Header (optional)"
																										placeholder="Trip details"
																										maxLength={60}
																									/>
																								</Col>
																								<Col>
																									<TextInputField
																										name={`steps[${index}].wa_flow_footer`}
																										label="Footer (optional)"
																										placeholder="Takes a minute"
																										maxLength={60}
																									/>
																								</Col>
																							</Grid>
																							<GetFieldValue<TWAFlow | null>
																								name={`steps[${index}].wa_flow`}
																							>
																								{({ value: pickedFlow }) => (
																									<SelectField
																										key={`${pickedFlow?.wa_business_account?.id || ""}:${pickedFlow?.id || ""}`}
																										select={SelectWAFlowScreen}
																										name={`steps[${index}].wa_flow_screen`}
																										label="Opening Screen"
																										flowId={pickedFlow?.id}
																										waBusinessAccountId={
																											pickedFlow
																												?.wa_business_account
																												?.id
																										}
																										autoSelectFirst
																										placeholder={
																											pickedFlow
																												? "Select the opening screen"
																												: "Pick the Flow first"
																										}
																										disabled={!pickedFlow}
																										help="The screen the form opens on. Meta requires one; the Flow's first screen is picked for you."
																									/>
																								)}
																							</GetFieldValue>
																							{source?.replies_into_conversation ? (
																								<Text
																									color="muted"
																									fontSize="sm"
																								>
																									Sent from the number the
																									customer wrote to.
																								</Text>
																							) : (
																								<SelectField
																									select={SelectWAPhoneNumber}
																									name={`steps[${index}].wa_phone_number`}
																									label="Send From"
																									placeholder="Select WhatsApp phone number"
																									allowAll
																								/>
																							)}
																						</Stack>
																					) : (
																						<Stack gap="3">
																							<Text fontWeight="semibold">
																								WhatsApp Delivery
																							</Text>
																							<>
																								<SelectField
																									select={SelectWATemplate}
																									name={`steps[${index}].wa_template`}
																									label="Template"
																									placeholder="Select WhatsApp template"
																									sourceKey={source?.key}
																									onChange={(value) => {
																										form.change(
																											`steps[${index}].wa_template` as never,
																											value
																										)
																										form.change(
																											`steps[${index}].wa_phone_number` as never,
																											null as never
																										)
																									}}
																								/>
																								<GetFieldValue<TWATemplate | null>
																									name={`steps[${index}].wa_template`}
																								>
																									{({ value: template }) => (
																										<>
																											<SyncAutomationTemplatePhoneNumber
																												form={form}
																												index={index}
																												template={template}
																											/>
																											{template &&
																											!source?.replies_into_conversation &&
																											getTemplatePhoneNumbers(
																												template
																											).length > 1 ? (
																												<GetFieldValue<TWhatsappBusinessAccountPhoneNumber | null>
																													name={`steps[${index}].wa_phone_number`}
																												>
																													{({
																														value: phoneNumber,
																													}) => (
																														<SelectInputField
																															name={`steps[${index}].wa_phone_number`}
																															label="Phone Number"
																															value={String(
																																phoneNumber?.id ||
																																	""
																															)}
																															onChange={(
																																event
																															) => {
																																const selectedPhoneNumber =
																																	getTemplatePhoneNumbers(
																																		template
																																	).find(
																																		(item) =>
																																			String(
																																				item.id
																																			) ===
																																			event
																																				.currentTarget
																																				.value
																																	) || null

																																form.change(
																																	`steps[${index}].wa_phone_number` as never,
																																	selectedPhoneNumber as never
																																)
																															}}
																														>
																															<option value="">
																																Select WhatsApp
																																phone number
																															</option>
																															{getTemplatePhoneNumbers(
																																template
																															).map((item) => (
																																<option
																																	key={item.id}
																																	value={String(
																																		item.id
																																	)}
																																>
																																	{item.name} ·{" "}
																																	{
																																		item.display_phone_number
																																	}
																																</option>
																															))}
																														</SelectInputField>
																													)}
																												</GetFieldValue>
																											) : template &&
																											  getTemplatePhoneNumbers(
																													template
																											  ).length === 1 ? (
																												<Alert
																													status="info"
																													hideIcon
																												>
																													Using{" "}
																													{
																														getTemplatePhoneNumbers(
																															template
																														)[0].name
																													}{" "}
																													·{" "}
																													{
																														getTemplatePhoneNumbers(
																															template
																														)[0]
																															.display_phone_number
																													}
																												</Alert>
																											) : template ? (
																												<Alert
																													status="warning"
																													title="No sending phone number found"
																												>
																													The selected template
																													business account does
																													not have an available
																													WhatsApp phone number.
																												</Alert>
																											) : null}
																										</>
																									)}
																								</GetFieldValue>
																							</>
																						</Stack>
																					)}
																				</Stack>
																			)
																		}
																	</GetFieldValue>
																</Stack>
															) : null}
														</Stack>
													</Box>
												)
											})}

											<Inline gap="3" flexWrap="wrap">
												<Button
													type="button"
													level="primary"
													onClick={() =>
														updateSteps((steps) => [
															...steps,
															createCommunicationStepFormValue(source),
														])
													}
												>
													<Icons.Plus /> Add Communication Step
												</Button>
												<Button
													type="button"
													level="secondary"
													onClick={() =>
														updateSteps((steps) => [
															...steps,
															createTaskStepFormValue(),
														])
													}
												>
													<Icons.Plus /> Add Task Step
												</Button>
												<Button
													type="button"
													level="secondary"
													onClick={() =>
														updateSteps((steps) => [
															...steps,
															createWaitStepFormValue(),
														])
													}
												>
													<Icons.Clock /> Add Wait Step
												</Button>
												<Button
													type="button"
													level="tertiary"
													onClick={() =>
														updateSteps((steps) => [
															...steps,
															createConditionStepFormValue(),
														])
													}
												>
													<Icons.Sliders /> Add Condition Step
												</Button>
											</Inline>
										</Stack>
									)}
								</GetFieldValue>
							</AutomationFormSection>

							{source?.available_parameters?.length ||
							source?.wildcard_field ? (
								<AutomationFormSection
									title="Available Parameters"
									description="These placeholders can be used in WhatsApp templates and task bodies."
								>
									<Stack gap="2">
										<Inline gap="2" flexWrap="wrap">
											{source.available_parameters.map((parameter) => (
												<Badge key={parameter.key}>
													{parameter.placeholder}
												</Badge>
											))}
										</Inline>
										{source.wildcard_field ? (
											<Text fontSize="sm" color="muted">
												Custom fields:{" "}
												<code>{`{{${source.wildcard_field.prefix}<your_key>}}`}</code>
												{source.wildcard_field.description
													? ` — ${source.wildcard_field.description}`
													: ""}
											</Text>
										) : null}
									</Stack>
								</AutomationFormSection>
							) : null}

							<Divider sm />
							<SubmissionError />
							<Inline gap="4">
								<Button
									type="button"
									level="tertiary"
									disabled={submitting}
									onClick={onCancel}
								>
									Cancel
								</Button>
								<Button type="submit" disabled={submitting}>
									{submitting
										? automation
											? "Updating..."
											: "Creating..."
										: automation
											? "Update Automation"
											: "Create Automation"}
								</Button>
							</Inline>
						</Stack>
					</form>
				)
			}}
		</Form>
	)
}

export function DeleteAutomationButton({
	automation,
	onSuccess,
}: {
	automation: TAutomation
	onSuccess: () => void
}) {
	const xhr = useXHR()
	const label = automation.source?.name || automation.source_key

	return (
		<Button
			size="sm"
			level="tertiary"
			status="warning"
			onClick={async () => {
				if (!window.confirm(`Delete automation "${label}"?`)) {
					return
				}

				await XHR(xhr).destroy(automation.id)
				showSnackbar("Automation deleted successfully.")
				onSuccess()
			}}
		>
			<Icons.Trash />
		</Button>
	)
}

export function ToggleAutomationButton({
	automation,
	onSuccess,
}: {
	automation: TAutomation
	onSuccess: (automation: TAutomation) => void
}) {
	const xhr = useXHR()
	const label = automation.source?.name || automation.source_key
	const isActive = automation.is_active

	return (
		<Button
			size="sm"
			level="tertiary"
			status={isActive ? "warning" : "success"}
			title={isActive ? "Deactivate" : "Activate"}
			onClick={async () => {
				if (
					isActive &&
					!window.confirm(
						`Deactivate automation "${label}"? It stops running until you activate it again.`
					)
				) {
					return
				}

				const resp = await XHR(xhr).toggle(automation.id)
				showSnackbar(
					resp.data.is_active
						? "Automation activated."
						: "Automation deactivated."
				)
				onSuccess(resp.data)
			}}
		>
			{isActive ? <Icons.Ban /> : <Icons.LightningBolt />}
		</Button>
	)
}
